/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { SemverValidator } from '@squiz/dxp-console-support';
import {
  BUTTON_FORMAT_SOFT,
  BUTTON_FORMAT_STRONG,
  BUTTON_THEME_DEFAULT,
  BUTTON_THEME_PRIMARY,
  Button,
  ButtonProps,
  CHIP_THEME_DEFAULT,
  CHIP_TYPE_REMOVABLE,
  ChipList,
  ChipProps,
  FORM_THEME_LITE,
  Form,
  FormControlOption,
  RadioButtons,
  SelectField,
  SelectFieldOption,
  SelectFieldProps,
  TextField,
  TextFieldProps,
} from '@squiz/sds';
import { sortVersions } from 'apps/component-service/src/Utilities/SortVersions/SortVersions';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { lastValueFrom } from 'rxjs';
import { COMPONENT_SERVICE_STRINGS } from '../../../../Constants/Strings.constants';
import { ComponentVersionsResult, getComponentService, useComponentVersions } from '../../../../Services/component';
import { ADD_COMPONENT_TO_SET_DATA_IDS } from '../../AddComponentToSet.constants';
import { SelectedComponentProps } from '../AvailableComponentsForm/SelectedComponentProps.props';
import { ComponentConfigurationFormProps } from './ComponentConfigurationForm.props';

/**
 * The form for configuring a component to add to set.
 */
export const ComponentConfigurationForm = (props: ComponentConfigurationFormProps): JSX.Element => {
  const { t } = useTranslation();
  const {
    close,
    setButtonKeys,
    setButtons,
    updateStep,
    updateComponentSetDetails,
    webpath,
    submitButtonText,
    currentExcludedVersions,
    currentRenderableFormOption,
    currentEditableFormOption,
  } = props;

  const VALUE_VERSIONS_ALL = '*';
  const VALUE_VERSIONS_LATEST = 'latest';

  // Selected component from previous step
  const selectedComponent = props.customProps as SelectedComponentProps;

  // Component service
  const componentService = getComponentService();

  const componentVersions: ComponentVersionsResult = useComponentVersions({ webpath: selectedComponent.componentID });

  // Labels
  const labels = useMemo(() => {
    return {
      addToSet: t(COMPONENT_SERVICE_STRINGS.addToSet),
      cancel: t(COMPONENT_SERVICE_STRINGS.cancel),
      confirmationButton: t(submitButtonText),
      editableVersioningTitle: t(COMPONENT_SERVICE_STRINGS.editableVersioningTitle),
      editableVersionsAdd: t(COMPONENT_SERVICE_STRINGS.editableVersionsAdd),
      editableVersionsLatest: t(COMPONENT_SERVICE_STRINGS.editableVersionsLatest),
      editableVersionsNone: t(COMPONENT_SERVICE_STRINGS.editableVersionsNone),
      editableVersionsSelect: t(COMPONENT_SERVICE_STRINGS.editableVersionsSelect),
      editableVersionsSpecify: t(COMPONENT_SERVICE_STRINGS.editableVersionsSpecify),
      excludedVersionsAdd: t(COMPONENT_SERVICE_STRINGS.excludedVersionsAdd),
      excludedVersionsSelect: t(COMPONENT_SERVICE_STRINGS.excludedVersionsSelect),
      excludeSpecificVersionsTitle: t(COMPONENT_SERVICE_STRINGS.excludeSpecificVersionsTitle),
      failure: t(COMPONENT_SERVICE_STRINGS.apiError),
      renderableVersionsAll: t(COMPONENT_SERVICE_STRINGS.renderableVersionsAll),
      renderableVersionsSpecify: t(COMPONENT_SERVICE_STRINGS.renderableVersionsSpecify),
      renderableVersionsTitle: t(COMPONENT_SERVICE_STRINGS.renderableVersionsTitle),
      select: t(COMPONENT_SERVICE_STRINGS.select),
      selectVersion: t(COMPONENT_SERVICE_STRINGS.selectVersion),
      specifyVersions: t(COMPONENT_SERVICE_STRINGS.specifyVersions),
      useLatestVersion: t(COMPONENT_SERVICE_STRINGS.useLatestVersion),
      versioning: t(COMPONENT_SERVICE_STRINGS.versioning),
      versionRules: t(COMPONENT_SERVICE_STRINGS.versionRules),
      versionRulesValid: t(COMPONENT_SERVICE_STRINGS.versionRulesValid),
    };
  }, [submitButtonText, t]);

  // Form input values
  const inputValues = { versionRules: `` };

  // Renderable versioning options
  const renderableVersioningOptions = useMemo((): Array<FormControlOption> => {
    return [
      {
        key: ADD_COMPONENT_TO_SET_DATA_IDS.renderableVersionsAll,
        label: labels.renderableVersionsAll,
        value: ADD_COMPONENT_TO_SET_DATA_IDS.renderableVersionsAll,
      },
      {
        key: ADD_COMPONENT_TO_SET_DATA_IDS.renderableVersionsSpecify,
        label: labels.renderableVersionsSpecify,
        value: ADD_COMPONENT_TO_SET_DATA_IDS.renderableVersionsSpecify,
      },
    ];
  }, [labels]);

  // Editable versioning options
  const editableVersioningOptions = useMemo((): Array<FormControlOption> => {
    return [
      {
        key: ADD_COMPONENT_TO_SET_DATA_IDS.editableVersionsLatest,
        label: labels.editableVersionsLatest,
        value: ADD_COMPONENT_TO_SET_DATA_IDS.editableVersionsLatest,
      },
      {
        key: ADD_COMPONENT_TO_SET_DATA_IDS.editableVersionsNone,
        label: labels.editableVersionsNone,
        value: ADD_COMPONENT_TO_SET_DATA_IDS.editableVersionsNone,
      },
      {
        key: ADD_COMPONENT_TO_SET_DATA_IDS.editableVersionsSpecific,
        label: labels.editableVersionsSpecify,
        value: ADD_COMPONENT_TO_SET_DATA_IDS.editableVersionsSpecific,
      },
    ];
  }, [labels]);

  // Specify version options
  let componentVersionsSelectOptions: Array<SelectFieldOption> = [];

  if (componentVersions[0]) {
    const sortedVersions = sortVersions(
      componentVersions[0].versions.map((version) => version.version),
      'desc',
    );
    componentVersionsSelectOptions = sortedVersions.map((version, index) => {
      return {
        key: String(index),
        label: version,
        value: version,
      };
    });
  }

  const [formError, setFormError] = useState<string>(``);
  const [isFormSubmitted, setSubmit] = useState<boolean>(false);
  const [chosenRenderableVersionOption, setChosenRenderableVersionOption] = useState<string | undefined>(
    renderableVersioningOptions[0].value,
  );
  const [renderVersionStringOnLoad, setRenderVersionStringOnLoad] = useState<string | null>(null);
  const [chosenEditableVersionOption, setChosenEditableVersionOption] = useState<string | undefined>(
    editableVersioningOptions[0].value,
  );
  const [chosenEditableVersionSelect, setChosenEditableVersionSelect] = useState<string | null>(null);
  const [editableVersions, setEditableVersions] = useState<Array<string>>([]);

  const [chosenExcludedVersionSelect, setChosenExcludedVersionSelect] = useState<string | null>(null);
  const [excludedVersions, setExcludedVersions] = useState<Array<string>>(currentExcludedVersions ?? []);
  const [renderableVersionsSpecifyError, setRenderableVersionsSpecifyError] = useState<string | null>(null);

  const editableVersionsSpecificVersionRef = useRef<HTMLInputElement>(null);
  const excludedVersionsSpecificVersionRef = useRef<HTMLInputElement>(null);
  const editableVersionsSpecificVersionButtonRef = useRef<HTMLButtonElement>(null);
  const excludedVersionsSpecificVersionButtonRef = useRef<HTMLButtonElement>(null);
  const renderableVersionRageRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (currentRenderableFormOption !== undefined) {
      if (typeof currentRenderableFormOption === 'string') {
        renderableVersioningOptions.forEach((option: FormControlOption) => {
          if (option.key === currentRenderableFormOption) {
            setChosenRenderableVersionOption(option.value);
          }
        });
      }
      if (typeof currentRenderableFormOption === 'object') {
        setChosenRenderableVersionOption(renderableVersioningOptions[1].value);
        const renderPattern = Object.values(currentRenderableFormOption)[0];
        setRenderVersionStringOnLoad(renderPattern);
      }
    }

    if (currentEditableFormOption !== undefined) {
      if (typeof currentEditableFormOption === 'string') {
        editableVersioningOptions.forEach((option: FormControlOption) => {
          if (option.key === currentEditableFormOption) {
            setChosenEditableVersionOption(option.value);
          }
        });
      }
      if (typeof currentEditableFormOption === 'object') {
        setChosenEditableVersionOption(editableVersioningOptions[2].value);
        const editableVersions = Object.values(currentEditableFormOption)[0];
        setEditableVersions(editableVersions);
      }
    }
  }, [currentEditableFormOption, currentRenderableFormOption, editableVersioningOptions, renderableVersioningOptions]);

  const semverValidator = useMemo(() => {
    return new SemverValidator<boolean>((): boolean => false);
  }, []);

  const renderableVersioningChangeHandler = (option: FormControlOption): void => {
    setChosenRenderableVersionOption(option.value);
  };

  const editableVersioningChangeHandler = (option: FormControlOption): void => {
    setChosenEditableVersionOption(option.value);
  };

  const editableVersionsSelectHandler = (option: FormControlOption): void => {
    setChosenEditableVersionSelect(option.value as string);
  };

  const excludedVersionsSelectHandler = (option: FormControlOption): void => {
    setChosenExcludedVersionSelect(option.value as string);
  };

  const submitHandler = useCallback((): void => {
    let renderableVersionPatternInput = renderableVersionRageRef.current?.value.trim() || '';
    if (chosenRenderableVersionOption === ADD_COMPONENT_TO_SET_DATA_IDS.renderableVersionsAll) {
      renderableVersionPatternInput = VALUE_VERSIONS_ALL;
    } else {
      if (!semverValidator.validate(renderableVersionPatternInput)) {
        setRenderableVersionsSpecifyError(labels.versionRulesValid);
        setSubmit(false);
        return;
      }
    }

    let editableVersionsInput = editableVersions;
    if (chosenEditableVersionOption === ADD_COMPONENT_TO_SET_DATA_IDS.editableVersionsLatest) {
      editableVersionsInput = [VALUE_VERSIONS_LATEST];
    } else if (chosenEditableVersionOption === ADD_COMPONENT_TO_SET_DATA_IDS.editableVersionsNone) {
      editableVersionsInput = [];
    }

    // Reset any form errors
    setFormError(``);

    lastValueFrom(componentService.getComponentSetDetails({ webpath }))
      .then((res) => {
        const componentSetDetails = JSON.parse(JSON.stringify(res));

        // This is the object we need to add the new component to
        const componentSetDto = {
          components: {},
          componentVersionRules: componentSetDetails.componentVersionRules,
          description: componentSetDetails.description,
          displayName: componentSetDetails.displayName,
          environmentVariables: componentSetDetails.environmentVariables,
          headers: componentSetDetails.headers,
        };

        // Add the component version rules
        componentSetDto.componentVersionRules[selectedComponent.componentID] = {
          editableVersions: editableVersionsInput,
          excludedVersions: excludedVersions,
          renderableVersionPattern: renderableVersionPatternInput,
        };

        // Send the PATCH request to update set with new component
        /* eslint-disable promise/no-nesting */
        return lastValueFrom(componentService.patchComponentSetDetails({ webpath, ...componentSetDto }))
          .then(() => {
            updateComponentSetDetails();
            return close();
          })
          .catch((err) => {
            // If error message show it otherwise default error
            setFormError(err?.response?.message || labels.failure);
            setSubmit(false);
          });
        /* eslint-enable promise/no-nesting */
      })
      .catch((err) => {
        // General error occurred adding component to set
        setFormError(labels.failure);
        setSubmit(false);
      });
  }, [
    chosenEditableVersionOption,
    chosenRenderableVersionOption,
    close,
    componentService,
    editableVersions,
    excludedVersions,
    labels,
    selectedComponent,
    semverValidator,
    updateComponentSetDetails,
    webpath,
  ]);

  // Set buttons
  useEffect(() => {
    const cancelButton = (
      <Button
        format={BUTTON_FORMAT_SOFT}
        onClick={(): void => close()}
        text={labels.cancel}
        theme={BUTTON_THEME_DEFAULT}
      />
    );

    const addToSetButton = (
      <Button
        data-id={ADD_COMPONENT_TO_SET_DATA_IDS.addToSetButton}
        format={BUTTON_FORMAT_STRONG}
        isLoading={isFormSubmitted}
        onClick={(): void => setSubmit(true)}
        text={labels.confirmationButton}
        theme={BUTTON_THEME_PRIMARY}
      />
    );

    if (isFormSubmitted) {
      setButtonKeys([`add`]);
      setButtons([addToSetButton]);
      submitHandler();
    } else {
      setButtonKeys([`cancel`, `add`]);
      setButtons([cancelButton, addToSetButton]);
    }
  }, [close, isFormSubmitted, labels, setButtonKeys, setButtons, submitHandler, updateStep]);

  const editableSpecifyVersionProps: SelectFieldProps = {
    callback: editableVersionsSelectHandler,
    classes: 'add-to-set__component-versions',
    fieldLabel: labels.editableVersionsSelect,
    hideLabel: true,
    id: ADD_COMPONENT_TO_SET_DATA_IDS.editableVersioningOptions,
    name: ADD_COMPONENT_TO_SET_DATA_IDS.editableVersioningOptions,
    options: componentVersionsSelectOptions,
    placeholder: labels.select,
  };

  const excludedSpecifyVersionProps: SelectFieldProps = {
    callback: excludedVersionsSelectHandler,
    classes: 'add-to-set__component-versions',
    fieldLabel: labels.excludedVersionsSelect,
    hideLabel: true,
    id: ADD_COMPONENT_TO_SET_DATA_IDS.excludedVersioningOptions,
    name: ADD_COMPONENT_TO_SET_DATA_IDS.excludedVersioningOptions,
    options: componentVersionsSelectOptions,
    placeholder: labels.select,
  };

  const renderableVersionsTextFieldProps: TextFieldProps = {
    errorMessage: renderableVersionsSpecifyError,
    fieldLabel: labels.renderableVersionsSpecify,
    hideLabel: true,
    id: ADD_COMPONENT_TO_SET_DATA_IDS.renderableVersioningSpecifyRangeInput,
    name: ADD_COMPONENT_TO_SET_DATA_IDS.renderableVersioningSpecifyRangeInput,
    value: inputValues.versionRules,
  };

  const editableVersionsAddButtonProfs: ButtonProps = {
    classes: 'add-to-set__add-to-list',
    format: BUTTON_FORMAT_SOFT,
    onClick: (): void => {
      if (chosenEditableVersionSelect?.length && !editableVersions.includes(chosenEditableVersionSelect)) {
        const newEditableVersions = sortVersions([...editableVersions, chosenEditableVersionSelect], 'desc');
        setEditableVersions(newEditableVersions);
      }
    },
    text: labels.editableVersionsAdd,
    theme: BUTTON_THEME_DEFAULT,
  };

  const editableVersionChips: Array<ChipProps> = editableVersions.map((version) => {
    return {
      customClick: (): void => {
        setEditableVersions(editableVersions.filter((ev) => ev !== version));
      },
      key: version,
      text: version,
      theme: CHIP_THEME_DEFAULT,
      type: CHIP_TYPE_REMOVABLE,
    };
  });

  const excludedVersionsAddButtonProfs: ButtonProps = {
    classes: 'add-to-set__add-to-list',
    format: BUTTON_FORMAT_SOFT,
    onClick: (): void => {
      if (chosenExcludedVersionSelect?.length && !excludedVersions.includes(chosenExcludedVersionSelect)) {
        const newExcludedVersions = sortVersions([...excludedVersions, chosenExcludedVersionSelect], 'desc');
        setExcludedVersions(newExcludedVersions);
      }
    },
    text: labels.excludedVersionsAdd,
    theme: BUTTON_THEME_DEFAULT,
  };

  const excludedVersionChips: Array<ChipProps> = excludedVersions.map((version) => {
    return {
      customClick: (): void => {
        setExcludedVersions(excludedVersions.filter((ev) => ev !== version));
      },
      key: version,
      text: version,
      theme: CHIP_THEME_DEFAULT,
      type: CHIP_TYPE_REMOVABLE,
    };
  });

  return (
    <div className="add-to-set__step-2" data-id={ADD_COMPONENT_TO_SET_DATA_IDS.addToSetStep2}>
      <Form classes="add-to-set-form" errorMessage={formError} legend={labels.addToSet} theme={FORM_THEME_LITE}>
        {/* Renderable versioning */}
        <div className="add-to-set__version add-to-set__renderable">
          <h4 className="add-to-set__heading">{labels.renderableVersionsTitle}</h4>

          <div className="add-to-set__versioning-options">
            <RadioButtons
              fieldLabel={ADD_COMPONENT_TO_SET_DATA_IDS.renderableVersioningOptions}
              hideLabel
              id={ADD_COMPONENT_TO_SET_DATA_IDS.renderableVersioningOptions}
              name={ADD_COMPONENT_TO_SET_DATA_IDS.renderableVersioningOptions}
              options={renderableVersioningOptions}
              radioCallback={renderableVersioningChangeHandler}
              value={chosenRenderableVersionOption}
            />
          </div>

          {chosenRenderableVersionOption === ADD_COMPONENT_TO_SET_DATA_IDS.renderableVersionsSpecify && (
            <div className="add-to-set__versioning-inputs">
              <TextField
                {...renderableVersionsTextFieldProps}
                ref={renderableVersionRageRef}
                value={renderVersionStringOnLoad ?? undefined}
              />
            </div>
          )}
        </div>

        {/* Editable versioning */}
        <div className="add-to-set__version add-to-set__editable">
          <h4 className="add-to-set__heading">{labels.editableVersioningTitle}</h4>

          <div className="add-to-set__versioning-options">
            <RadioButtons
              fieldLabel={ADD_COMPONENT_TO_SET_DATA_IDS.editableVersioningOptions}
              hideLabel
              id={ADD_COMPONENT_TO_SET_DATA_IDS.editableVersioningOptions}
              name={ADD_COMPONENT_TO_SET_DATA_IDS.editableVersioningOptions}
              options={editableVersioningOptions}
              radioCallback={editableVersioningChangeHandler}
              value={chosenEditableVersionOption}
            />
            {chosenEditableVersionOption === ADD_COMPONENT_TO_SET_DATA_IDS.editableVersionsSpecific && (
              <div className="add-to-set__versioning-inputs">
                <ChipList chips={editableVersionChips} theme={CHIP_THEME_DEFAULT} />
                <SelectField {...editableSpecifyVersionProps} ref={editableVersionsSpecificVersionRef} />
                <Button {...editableVersionsAddButtonProfs} ref={editableVersionsSpecificVersionButtonRef} />
              </div>
            )}
          </div>
        </div>

        {/* Exclude versions */}
        <div className="add-to-set__version add-to-set__excluded">
          <h4 className="add-to-set__heading">{labels.excludeSpecificVersionsTitle}</h4>
          <div className="add-to-set__versioning-inputs">
            <ChipList chips={excludedVersionChips} theme={CHIP_THEME_DEFAULT} />
            <SelectField {...excludedSpecifyVersionProps} ref={excludedVersionsSpecificVersionRef} />
            <Button {...excludedVersionsAddButtonProfs} ref={excludedVersionsSpecificVersionButtonRef} />
          </div>
        </div>
      </Form>
    </div>
  );
};
